/**
 * Overview metric cards displayed at the top of the dashboard.
 * Shows total counts for assets, scans, findings by severity, etc.
 */

import { ShieldAlert, Server, Radar, AlertTriangle } from 'lucide-react';
import type { FindingSummary } from '@/types/finding';

interface SummaryCardsProps {
  totalAssets: number;
  totalScans: number;
  findingSummary: FindingSummary | null;
}

interface CardData {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

export default function SummaryCards({ totalAssets, totalScans, findingSummary }: SummaryCardsProps) {
  const cards: CardData[] = [
    {
      label: 'Total Assets',
      value: totalAssets,
      icon: <Server className="h-6 w-6" />,
      color: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Total Scans',
      value: totalScans,
      icon: <Radar className="h-6 w-6" />,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Critical Findings',
      value: findingSummary?.by_severity?.critical ?? 0,
      icon: <ShieldAlert className="h-6 w-6" />,
      color: 'text-red-600 bg-red-50',
    },
    {
      label: 'Open Findings',
      value: findingSummary?.open ?? 0,
      icon: <AlertTriangle className="h-6 w-6" />,
      color: 'text-orange-600 bg-orange-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="card flex items-center gap-4">
          <div className={`rounded-lg p-3 ${card.color}`}>{card.icon}</div>
          <div>
            <p className="text-sm font-medium text-gray-500">{card.label}</p>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
